import React, {useEffect, useState} from "react";
import axios from "axios";
import ReportTable from "./common/ReportTable";
import TableSkeleton from "../utils/TableSkeleton";
import EmptyTableState from "../utils/EmptyTableState";
import * as Constants from "../utils/Constants";
import "../styles/components/reports.css";
import {formatDateToDDMMYYYY, getAuthToken, getClientIdOfUser, getGeolocationFromLatLng,} from "../utils/utility";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import {CardList} from "./CardView";
import {customStyles} from "../utils/DataTableCustomStyle";
import {convertUtcToLocal} from "../utils/dateUtils";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBolt,
    faBuilding,
    faCircleInfo,
    faClock,
    faCompass,
    faCubes,
    faFileExcel,
    faFilePdf,
    faHashtag,
    faImage,
    faLocationDot,
    faLock,
    faTableList,
    faTag,
} from "@fortawesome/free-solid-svg-icons";

const BTLockUnlockReport = () => {
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [reports, setReports] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [assetList, setAssetList] = useState([]);
    const [checkedItems, setCheckedItems] = useState({});
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(50);

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = "/";
    };

    const ExpandedComponent = ({data}) => {
        const formatLabel = (key) =>
            key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

        const getIcon = (key) => {
            const k = key.toLowerCase();
            if (k === "id") return faHashtag;
            if (k.includes("assetunitid") || k.includes("deviceid") || k.includes("devicename")) return faCubes;
            if (k.includes("name")) return faTag;
            if (k.includes("lock") || k.includes("unlock")) return faLock;
            if (k.includes("event")) return faBolt;
            if (k.includes("time") || k.includes("date")) return faClock;
            if (k.includes("location") || k.includes("address")) return faLocationDot;
            if (k.includes("lat") || k.includes("lon") || k.includes("lng")) return faCompass;
            if (k.includes("image") || k.includes("img") || k.includes("imageurl")) return faImage;
            if (k.includes("client")) return faBuilding;
            return faCircleInfo;
        };

        const getIconStyle = (key) => {
            const k = key.toLowerCase();
            if (k.includes("lock") || k.includes("unlock")) return {background: "#fff1f2", color: "#ff683a"};
            if (k.includes("time") || k.includes("date")) return {background: "#eff6ff", color: "#3b82f6"};
            if (k.includes("location") || k.includes("lat") || k.includes("lon")) return {
                background: "#f0fdf4",
                color: "#10b981"
            };
            if (k.includes("event")) return {background: "#fff7ed", color: "#f59e0b"};
            if (k === "id") return {background: "#f5f3ff", color: "#7c3aed"};
            if (k.includes("name") || k.includes("asset")) return {background: "#fef9ee", color: "#d97706"};
            if (k.includes("image") || k.includes("img")) return {background: "#fdf4ff", color: "#a855f7"};
            return {background: "var(--color-primary-ghost)", color: "var(--color-primary)"};
        };

        const formatValue = (key, value) => {
            if (key === "eventTime") return convertUtcToLocal(value) || "\u2014";
            if (typeof value === "boolean") return value ? "Yes" : "No";
            if (value === null || value === undefined || value === "") return "\u2014";
            return String(value);
        };

        return (
            <div className="expanded-row-content">
                <div className="expanded-row-header">
                    <FontAwesomeIcon icon={faTableList}/>
                    Full Record Details
                </div>
                <div className="expanded-grid">
                    {Object.entries(data).map(([key, value]) => {
                        const iconStyle = getIconStyle(key);
                        return (
                            <div key={key} className="expanded-item">
                                <div className="expanded-item-icon" style={iconStyle}>
                                    <FontAwesomeIcon icon={getIcon(key)}/>
                                </div>
                                <div className="expanded-item-body">
                                    <div className="expanded-item-label">{formatLabel(key)}</div>
                                    <div className="expanded-item-value">{formatValue(key, value)}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const columns = [
        {name: "Report ID", id: "Report ID", selector: (row) => row.id, sortable: true, width: "100px"},
        {name: "Asset ID", id: "Asset ID", selector: (row) => row.assetUnitId, sortable: true, width: "140px"},
        {name: "Name", id: "Name", selector: (row) => row.assetName || "N/A", sortable: true, width: "150px", wrap: true},
        {
            name: "Date-Time", id: "Date-Time",
            selector: (row) => row.eventTime,
            sortable: true,
            width: "180px",
            format: (row) => convertUtcToLocal(row.eventTime)
        },
        {
            name: "Location", id: "Location",
            selector: (row) => row.location,
            grow: 1,
            minWidth: "200px",
            wrap: true,
            format: (row) => row.location ? row.location : "N/A"
        },
        {
            name: "Event", id: "Event",
            selector: (row) => row.eventType,
            width: "140px",
            cell: (row) => {
                const event = row.eventType?.toLowerCase() || "";
                const isLockEvent = event.includes("lock") && !event.includes("unlock");
                const isUnlockEvent = event.includes("unlock") || event.includes("opened");
                const badgeClass = isLockEvent ? "locked" : isUnlockEvent ? "unlocked" : "";
                if (badgeClass) {
                    return (
                        <span className={`lock-status-badge ${badgeClass}`}>
              <FontAwesomeIcon icon={isLockEvent ? faLock : faLock}/>
                            {row.eventType || "N/A"}
            </span>
                    );
                }
                return (
                    <span style={{
                        padding: "3px 10px",
                        borderRadius: "12px",
                        backgroundColor: "#E3F2FD",
                        color: "#0D47A1",
                        fontWeight: 600,
                        fontSize: "11px"
                    }}>
            {row.eventType || "N/A"}
          </span>
                );
            },
        },
        {
            name: "Img", id: "Img",
            selector: (row) => row.imageUrl,
            width: "80px",
            cell: (row) => row.imageUrl ? <button className="btn-export" style={{padding: '2px 6px', fontSize: '10px'}}
                                                  onClick={() => handleViewImage(row.imageUrl)}>View</button> : "-"
        }
    ];

    const handleViewImage = async (imagePath) => {
        try {
            const payload = {imagePath};
            const response = await axios.post(`${Constants.BASE_URL}api/v1/bt-lock-unlock-reports/image`, payload, {
                headers: {Authorization: `${Constants.BEARER} ${getAuthToken()}`, "Content-Type": "application/json"},
                responseType: "blob",
            });
            const fileURL = URL.createObjectURL(response.data);
            window.open(fileURL, "_blank");
        } catch (error) {
            alert("Unable to load image.");
        }
    };

    const getUserRole = () => {
        return localStorage.getItem("role");
    };

    const getUserId = () => {
        const loggedInUser = JSON.parse(localStorage.getItem(Constants.LOGGED_IN_USER));
        return loggedInUser?.userId;
    };

    const fetchAssignedAssetsForUser = async (userId) => {
        try {
            const endpoint = `${Constants.API_V1}/${getClientIdOfUser()}/asset-management/user/${userId}`;
            axios.defaults.headers.common["Authorization"] = `Bearer ${getAuthToken()}`;

            const response = await axios.get(`${Constants.BASE_URL}${endpoint}`);
            const raw = Array.isArray(response.data) ? response.data : [];

            const assignedAssetIds = raw
                .filter(a => !a.returnDate)
                .map(a => a.asset?.assetId)
                .filter(Boolean);

            return assignedAssetIds;
        } catch (err) {
            console.error("Error fetching assigned assets:", err);
            return [];
        }
    };

    const onSearchClick = () => {
        if (Object.keys(checkedItems).filter((id) => checkedItems[id]).length === 0 && (!startDate || !endDate)) {
            alert("Select Asset and/or Date Range.");
            return;
        }
        setCurrentPage(1);
        handleSearch(1, perPage);
    };


    const handleSearch = async (page = 1, size = perPage) => {
        setIsLoading(true);
        try {

            axios.defaults.headers.common["Authorization"] = `Bearer ${getAuthToken()}`;
            const clientId = getClientIdOfUser();
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const checkedAssetIds = Object.keys(checkedItems).filter((id) => checkedItems[id]);

            const params = new URLSearchParams();
            params.set("clientId", clientId);
            if (checkedAssetIds.length > 0) {
                checkedAssetIds.forEach(id => params.append("assetUnitIds", id));
            }
            if (startDate && endDate) {
                params.set("from", startDate);
                params.set("to", endDate);
            }
            params.set("timezone", timezone);
            params.set("page", page - 1);
            params.set("size", size);

            const response = await axios.get(`${Constants.BASE_URL}api/v1/bt-lock-unlock-reports?${params.toString()}`);
            if (response.status === 200) {
                const pageData = response.data.responseData;
                let data = Array.isArray(pageData?.content) ? pageData.content : (Array.isArray(pageData) ? pageData : []);
                setTotalRows(pageData?.totalElements ?? data.length);
                data = await updateLocationForReports(data);
                data = updateAssetNameInReport(assetList, data);
                setReports(data);
            } else if (response.status === 403) {
                handleLogout();
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        handleSearch(page, perPage);
    };

    const handlePerRowsChange = (newSize, page) => {
        setPerPage(newSize);
        setCurrentPage(page);
        handleSearch(page, newSize);
    };

    const updateAssetNameInReport = (assets, reports) => {
        return reports.map((r) => {
            const asset = assets.find((a) => a.assetUnitId === r.assetUnitId);
            return {...r, assetName: asset ? asset.name : "Unknown"};
        });
    };

    async function updateLocationForReports(reports) {
        return Promise.all(
            reports.map(async (report) => {
                if (report.latitude && report.longitude) {
                    const loc = await getGeolocationFromLatLng(report.latitude, report.longitude);
                    return {...report, location: loc || "Location not available"};
                }
                return report;
            })
        );
    }

    const fetchData = async () => {
        try {
            const endPoint = `${Constants.API_V1}/${getClientIdOfUser()}/${Constants.EP_ASSETS_GET_ALL}`;
            axios.defaults.headers.common["Authorization"] = `Bearer ${getAuthToken()}`;
            const res = await axios.get(`${Constants.BASE_URL}${endPoint}`);

            if (res.status === 200) {
                let allAssets = res.data.responseData || [];

                // Filter only BT_LOCK assets
                const btLockAssets = allAssets.filter(
                    asset => (asset.assetType || "").toUpperCase() === "BT_LOCK"
                );

                const userRole = getUserRole();
                const userId = getUserId();

                // If user is CLIENT_ADMIN or CLIENT_MANAGER, return all BT_LOCK assets
                if (userRole === "CLIENT_ADMIN" || userRole === "CLIENT_MANAGER") {
                    return btLockAssets;
                }

                // For other roles, fetch assigned assets and filter
                const assignedAssetIds = await fetchAssignedAssetsForUser(userId);

                const filteredAssets = btLockAssets.filter(asset =>
                    assignedAssetIds.includes(asset.assetId)
                );

                return filteredAssets;
            }
        } catch (err) {
            console.error("Error fetching assets:", err);
            handleLogout();
        }
        return [];
    };

    const exportToPDF = () => {
        const doc = new jsPDF("l", "pt", "a4");

        try {
            const img = new Image();
            img.src = require("../assets/yekkada-logo-new.png");
            doc.addImage(img, "PNG", 20, 20, 80, 30);
        } catch (e) {
        }

        doc.setFontSize(14);
        doc.text("BT Lock/Unlock Reports", 120, 40);

        const tableData = reports.map((r) => [
            r.id,
            r.assetUnitId,
            r.assetName,
            r.eventType || "-",
            convertUtcToLocal(r.eventTime),
            r.location?.substring(0, 90) || "N/A",
        ]);

        autoTable(doc, {
            startY: 80,
            head: [["ID", "Asset ID", "Name", "Event", "Date-Time", "Location"]],
            body: tableData,
            styles: {fontSize: 8},
            theme: "grid",
            columnStyles: {
                0: {cellWidth: 50},
                1: {cellWidth: 80},
                2: {cellWidth: 90},
                3: {cellWidth: 75},
                4: {cellWidth: 100},
                5: {cellWidth: 200},
            },
        });

        doc.save("bt_lock_unlock_reports.pdf");
    };

    const exportToExcel = () => {
        const ws = XLSX.utils.json_to_sheet(reports);
        const wb = {Sheets: {data: ws}, SheetNames: ["data"]};
        const excelBuffer = XLSX.write(wb, {bookType: "xlsx", type: "array"});
        FileSaver.saveAs(new Blob([excelBuffer]), "bt_lock_unlock_reports.xlsx");
    };

    const handleClear = () => {
        setStartDate("");
        setEndDate("");
        setReports([]);
        setCheckedItems({});
        setTotalRows(0);
        setCurrentPage(1);
    };

    useEffect(() => {
        fetchData().then((data) => setAssetList(Array.isArray(data) ? data : []));
    }, []);

    const handleCheckboxToggle = (assetUnitId) => {
        setCheckedItems((prev) => ({...prev, [assetUnitId]: !prev[assetUnitId]}));
    };

    const paginationComponentOptions = {
        rowsPerPageText: "Rows per page:",
        rangeSeparatorText: "of",
        selectAllRowsItem: true,
        selectAllRowsItemText: "All",
    };

    return (
        <div className="report-content-split">
            <div className="report-main-area">
                <div className="report-header-actions">
                <span className="report-results-title">
                    BT Lock/Unlock Results {totalRows > 0 && `(${totalRows})`}
                </span>
                    {reports.length > 0 && (
                        <div className="export-buttons">
                            <button className="export-btn" onClick={exportToExcel}><FontAwesomeIcon
                                icon={faFileExcel}/> Excel
                            </button>
                            <button className="export-btn" onClick={exportToPDF}><FontAwesomeIcon icon={faFilePdf}/> PDF
                            </button>
                        </div>
                    )}
                </div>

                <div className="report-table-wrapper">
                    <ReportTable
                        columns={columns}
                        data={reports}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationPerPage={perPage}
                        paginationRowsPerPageOptions={[25, 50, 100]}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        paginationComponentOptions={paginationComponentOptions}
                        customStyles={customStyles}
                        expandableRows
                        expandableRowsComponent={ExpandedComponent}
                        progressPending={isLoading}
                        progressComponent={<TableSkeleton/>}
                        fixedHeader={true}
                        fixedHeaderScrollHeight="100%"
                        subHeaderWrap={false}
                        noDataComponent={<EmptyTableState variant="search"/>}
                    />
                </div>
            </div>

            <div className="report-filter-sidebar">
                <div className="sidebar-header">
                    <h3 className="sidebar-title">Search Filters</h3>
                </div>

                <div className="sidebar-content">
                    <div className="filter-group">
                        <label className="filter-label">Select Assets</label>
                        <div className="asset-list-wrapper">
                            <CardList
                                data={assetList}
                                checkedItems={checkedItems}
                                toggleCheckbox={handleCheckboxToggle}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="filter-label">Start Date</label>
                        <input
                            type="date"
                            className="filter-input"
                            max={new Date().toISOString().split("T")[0]}
                            onChange={(e) => setStartDate(formatDateToDDMMYYYY(e.target.value))}
                        />
                    </div>
                    <div>
                        <label className="filter-label">End Date</label>
                        <input
                            type="date"
                            className="filter-input"
                            onChange={(e) => setEndDate(formatDateToDDMMYYYY(e.target.value))}
                        />
                    </div>
                </div>

                <div className="sidebar-footer">
                    <div className="filter-actions">
                        <button className="btn-report" onClick={() => {
                            setCurrentPage(1);
                            onSearchClick();
                        }}>
                            {Constants.FETCH_REPORTS}
                        </button>
                        <button className="btn-report secondary" onClick={handleClear}>
                            {Constants.CLEAR_FILTERS}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BTLockUnlockReport;
